import { Injectable, inject } from '@angular/core';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  InvoicingRequest,
  InvoicingRequestStatus,
  invoicingStoragePaths,
} from '../../models/invoicing-request.type';
import { CfdiUseCode, isCfdiUseCode } from '../../models/user-profile.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { FirebaseStorageService } from '../storage/firebase-storage.service';

const COLLECTION = 'invoicingRequests';

@Injectable()
export class InvoicingRequestService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private storage = inject(FirebaseStorageService);

  watch(uid: string, onValue: (request: InvoicingRequest | null) => void): () => void {
    return onSnapshot(doc(this.firestore, COLLECTION, uid), (snap) => {
      onValue(snap.exists() ? parseInvoicingRequest(snap.id, snap.data()) : null);
    });
  }

  async listPending(): Promise<InvoicingRequest[]> {
    const snapshot = await getDocs(
      query(collection(this.firestore, COLLECTION), where('status', '==', 'pending'))
    );
    return snapshot.docs.map((item) => parseInvoicingRequest(item.id, item.data()));
  }

  /**
   * Stores the constancia, e.firma files, and password where only a platform admin can read them.
   * The request stays pending until an admin marks it verified.
   */
  async submit(input: {
    uid: string;
    businessId: string;
    businessName: string;
    cfdiUse: CfdiUseCode;
    constancia: File;
    certificate: File;
    privateKey: File;
    privateKeyPassword: string;
  }): Promise<void> {
    const paths = invoicingStoragePaths(input.businessId, input.uid);
    const passwordFile = new File([input.privateKeyPassword], 'efirma-password.txt', {
      type: 'text/plain',
    });
    await this.storage.addFile(input.constancia, paths.csf);
    await this.storage.addFile(input.certificate, paths.certificate);
    await this.storage.addFile(input.privateKey, paths.privateKey);
    await this.storage.addFile(passwordFile, paths.password);
    await setDoc(doc(this.firestore, COLLECTION, input.uid), {
      uid: input.uid,
      businessId: input.businessId,
      businessName: input.businessName,
      cfdiUse: input.cfdiUse,
      status: 'pending',
      csfPath: paths.csf,
      certificatePath: paths.certificate,
      privateKeyPath: paths.privateKey,
      passwordPath: paths.password,
      submittedAt: serverTimestamp(),
    });
  }

  async markVerified(uid: string): Promise<void> {
    await updateDoc(doc(this.firestore, COLLECTION, uid), {
      status: 'verified',
      verifiedAt: serverTimestamp(),
    });
  }

  downloadUrl(storagePath: string): Promise<string> {
    return this.storage.download(storagePath);
  }

  saveFile(storagePath: string, fileName: string): Promise<void> {
    return this.storage.saveFile(storagePath, fileName);
  }

  readText(storagePath: string): Promise<string> {
    return this.storage.readText(storagePath);
  }

  readBytes(storagePath: string): Promise<Uint8Array> {
    return this.storage.readBytes(storagePath);
  }
}

function parseInvoicingRequest(id: string, data: Record<string, unknown>): InvoicingRequest {
  const status: InvoicingRequestStatus = data['status'] === 'verified' ? 'verified' : 'pending';
  return {
    uid: typeof data['uid'] === 'string' ? data['uid'] : id,
    businessId: typeof data['businessId'] === 'string' ? data['businessId'] : '',
    businessName: typeof data['businessName'] === 'string' ? data['businessName'] : '',
    cfdiUse: isCfdiUseCode(data['cfdiUse']) ? data['cfdiUse'] : 'S01',
    status,
    csfPath: typeof data['csfPath'] === 'string' ? data['csfPath'] : '',
    certificatePath: typeof data['certificatePath'] === 'string' ? data['certificatePath'] : '',
    privateKeyPath: typeof data['privateKeyPath'] === 'string' ? data['privateKeyPath'] : '',
    passwordPath: typeof data['passwordPath'] === 'string' ? data['passwordPath'] : '',
    submittedAt: toDate(data['submittedAt']),
    verifiedAt: toDate(data['verifiedAt']),
  };
}

function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return null;
}
